// Low-level board representation and chain/liberty computation.
// Pure TS, no platform imports. Positions are immutable snapshots.

import type { Color, PointState, Pt } from '../types.ts';

export const EMPTY = 0 as const satisfies PointState;
export const BLACK = 1 as const satisfies PointState;
export const WHITE = 2 as const satisfies PointState;

/** Immutable board snapshot. `stones` is never mutated after construction. */
export interface Position {
  readonly size: number;
  readonly stones: Uint8Array;                // length size*size, values PointState
  readonly koPoint: Pt | null;                // point forbidden to the next mover (simple ko)
}

export function emptyPosition(size: number): Position {
  if (!Number.isInteger(size) || size < 1) throw new Error(`emptyPosition: bad size ${size}`);
  return { size, stones: new Uint8Array(size * size), koPoint: null };
}

export function clonePosition(pos: Position): Position {
  return { size: pos.size, stones: pos.stones.slice(), koPoint: pos.koPoint };
}

export function colorToState(color: Color): PointState {
  return color === 'B' ? BLACK : WHITE;
}

export function stateToColor(state: number): Color | null {
  return state === BLACK ? 'B' : state === WHITE ? 'W' : null;
}

export function oppositeState(state: PointState): PointState {
  return state === BLACK ? WHITE : state === WHITE ? BLACK : EMPTY;
}

/** Orthogonal neighbors of a point (2–4 entries). */
export function neighborsOf(size: number, pt: Pt): Pt[] {
  const x = pt % size;
  const y = (pt - x) / size;
  const out: Pt[] = [];
  if (x > 0) out.push(pt - 1);
  if (x < size - 1) out.push(pt + 1);
  if (y > 0) out.push(pt - size);
  if (y < size - 1) out.push(pt + size);
  return out;
}

export interface Chain {
  color: PointState;                          // BLACK or WHITE
  points: Pt[];                               // ascending order
  liberties: Pt[];                            // ascending order
}

/** Flood-fill the chain containing `start` (must hold a stone). */
export function chainAt(stones: Uint8Array, size: number, start: Pt): Chain {
  const color = stones[start];
  if (color !== BLACK && color !== WHITE) {
    throw new Error(`chainAt: no stone at pt ${start}`);
  }
  const inChain = new Set<Pt>([start]);
  const liberties = new Set<Pt>();
  const stack: Pt[] = [start];
  while (stack.length > 0) {
    const pt = stack.pop()!;
    for (const nb of neighborsOf(size, pt)) {
      const state = stones[nb];
      if (state === EMPTY) {
        liberties.add(nb);
      } else if (state === color && !inChain.has(nb)) {
        inChain.add(nb);
        stack.push(nb);
      }
    }
  }
  return {
    color,
    points: [...inChain].sort((a, b) => a - b),
    liberties: [...liberties].sort((a, b) => a - b),
  };
}

/** All chains on the board, each stone visited once. */
export function allChains(stones: Uint8Array, size: number): Chain[] {
  const visited = new Uint8Array(size * size);
  const chains: Chain[] = [];
  for (let pt = 0; pt < size * size; pt++) {
    if (visited[pt] || stones[pt] === EMPTY) continue;
    const chain = chainAt(stones, size, pt);
    for (const p of chain.points) visited[p] = 1;
    chains.push(chain);
  }
  return chains;
}
